// Tab shell for the per-major Chromium drill-in. Owns the URL <-> active-tab
// sync via `?tab=`. Each tab body is its own component so this stays
// strictly a layout module; counts are passed through so the operator can
// see "Profiles (3)" without opening the tab.

import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@veil/ui/components/tabs";
import { useMemo } from "react";
import { useLocation, useSearch } from "wouter";

export type ChromiumMajorTab = "builds" | "upstream" | "profiles";

interface Props {
	defaultTab?: ChromiumMajorTab;
	counts: { builds: number; upstream: number; profiles: number };
	builds: React.ReactNode;
	upstream: React.ReactNode;
	profiles: React.ReactNode;
}

const ALLOWED: ChromiumMajorTab[] = ["builds", "upstream", "profiles"];

function parseTab(
	search: string,
	fallback: ChromiumMajorTab,
): ChromiumMajorTab {
	const params = new URLSearchParams(search);
	const t = params.get("tab");
	if (t && (ALLOWED as string[]).includes(t)) return t as ChromiumMajorTab;
	return fallback;
}

export function ChromiumMajorTabs({
	defaultTab = "builds",
	counts,
	builds,
	upstream,
	profiles,
}: Props) {
	const search = useSearch();
	const [location, setLocation] = useLocation();
	const active = useMemo(
		() => parseTab(search, defaultTab),
		[search, defaultTab],
	);

	function onChange(next: string) {
		const params = new URLSearchParams(search);
		params.set("tab", next);
		// wouter setLocation accepts the path portion; keep current pathname.
		setLocation(`${location}?${params.toString()}`);
	}

	return (
		<Tabs value={active} onValueChange={onChange} className="gap-4">
			<TabsList>
				<TabsTrigger value="builds">Builds ({counts.builds})</TabsTrigger>
				<TabsTrigger value="upstream">Upstream ({counts.upstream})</TabsTrigger>
				<TabsTrigger value="profiles">Profiles ({counts.profiles})</TabsTrigger>
			</TabsList>
			<TabsContent value="builds">{builds}</TabsContent>
			<TabsContent value="upstream">{upstream}</TabsContent>
			<TabsContent value="profiles">{profiles}</TabsContent>
		</Tabs>
	);
}
